import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { HeaderLogo } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Layout";
import { errMsg } from "@/lib/api";
import { adminAuth, setAdminSession } from "@/lib/adminApi";
import NotFound from "@/pages/NotFound";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen bg-ink text-white">
    <header className="flex h-14 items-center px-4"><HeaderLogo className="h-6 brightness-0 invert" /></header>
    <main className="mx-auto max-w-sm px-4 py-10">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-light"><ShieldCheck size={14} /> Platform admin</div>
      <h1 className="mt-1 text-3xl font-black">{title}</h1>
      {sub && <p className="mt-1 text-sm text-white/70">{sub}</p>}
      {children}
    </main>
  </div>
);

const Field = ({ label, ...p }) => (
  <label className="mt-4 block"><span className="text-sm font-semibold">{label}</span><Input className="mt-1 bg-white text-ink" {...p} /></label>
);

export function AdminLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value });
  useEffect(() => { document.title = "Admin sign-in · LineGone"; if (params.get("expired")) toast.message("Your admin session ended after 30 minutes of inactivity."); }, [params]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await adminAuth.post("/login", f); setAdminSession(r.data.token); nav("/admin"); }
    catch (err) { toast.error(errMsg(err)); setF({ ...f, password: "", code: "" }); }
    setBusy(false);
  };
  return (
    <Shell title="Sign in" sub="Email, password and your authenticator app code.">
      <form onSubmit={submit} data-testid="admin-login-form">
        <Field label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="username" data-testid="admin-login-email" />
        <Field label="Password" type="password" value={f.password} onChange={set("password")} autoComplete="current-password" data-testid="admin-login-password" />
        <Field label="6-digit authenticator code" inputMode="numeric" value={f.code} onChange={set("code")} autoComplete="one-time-code" data-testid="admin-login-code" />
        <button className="btn-primary mt-6 w-full" disabled={busy || f.code.length !== 6 || f.password.length < 12} data-testid="admin-login-submit">Sign in</button>
      </form>
    </Shell>
  );
}

export function AdminSetup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(undefined);
  const [f, setF] = useState({ password: "", confirm: "", code: "" });
  useEffect(() => { adminAuth.get("/setup", { params: { token } }).then((r) => setInfo(r.data)).catch(() => setInfo(null)); }, [token]);
  if (info === undefined) return <Spinner />;
  if (info === null) return <NotFound />;
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    try { await adminAuth.post("/setup", { token, password: f.password, code: f.code }); toast.success("Admin account ready. Sign in now."); nav("/admin/login"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Set up your admin account" sub={`For ${info.email}. This link works once.`}>
      <form onSubmit={submit} data-testid="admin-setup-form">
        <div className="mt-6 rounded-2xl bg-white p-4 text-ink">
          <p className="text-sm font-semibold">1. Scan with your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…)</p>
          <img src={info.qr} alt="Two-factor QR code" className="mx-auto mt-3 h-48 w-48" data-testid="admin-setup-qr" />
          <p className="mt-2 break-all text-center font-mono text-xs" data-testid="admin-setup-secret">Or enter key: {info.secret}</p>
        </div>
        <Field label="2. Choose a password (12+ characters, mix of upper, lower, numbers, symbols)" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" data-testid="admin-setup-password" />
        <Field label="Confirm password" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} autoComplete="new-password" data-testid="admin-setup-confirm" />
        <Field label="3. Enter the 6-digit code from the app" inputMode="numeric" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="admin-setup-code" />
        <button className="btn-primary mt-6 w-full" disabled={f.password.length < 12 || f.code.length !== 6} data-testid="admin-setup-submit">Finish setup</button>
      </form>
    </Shell>
  );
}
